import React, { useEffect, useRef, useState, useCallback } from "react";

import { useLocation } from "react-router-dom";
import { Field, Form, Formik, useFormikContext } from "formik";

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  RadioGroup,
  Text,
  Stack,
  Checkbox,
  useBreakpointValue,
  Tooltip,
  Icon,
} from "@chakra-ui/react";

import * as Yup from "yup";
import { CustomSelectWField } from "../CustomSelectWField";

import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RelatorioService from "../../../main/services/RelatorioService";
import MotivoSituacaoService from "../../../main/services/MotivoSituacaoService";
import ServicoAcolhimentoService from "../../../main/services/ServicoAcolhimentoService";
import JustificativaService from "../../../main/services/JustificativaService";
import { criancaFunc } from "../../../helpers/Funcionalidades";

import { isValid, parse, isAfter, isEqual } from "date-fns";
import { CustomMultiSelectWField } from "../MultiSelect";
import { RadioButtonField } from "../RadioButtonField";
import { CustomDatepickerWField } from "../CustomDatepickerWField";
import { useAuth } from "react-oidc-context";
import { PopoverGenero } from "../PopoverGenero";
import { IoHelpCircle } from "react-icons/io5";

const schema = Yup.object().shape({
  universoConsulta: Yup.object()
    .nullable()
    .required("Este campo é obrigatório!"),

  tipoPeriodo: Yup.string().when(["universoConsulta", "idTipoTransferencia"], {
    is: (universoConsulta, idTipoTransferencia) =>
      universoConsulta?.id !== 2 && idTipoTransferencia?.id !== 2,
    then: (schema) => schema.required("Este campo é obrigatório!"),
    otherwise: (schema) => schema.nullable(),
  }),

  idTipoTransferencia: Yup.object()
    .nullable()
    .when("universoConsulta", {
      is: (universoConsulta) => universoConsulta?.id === 3,
      then: (schema) => schema.required("Este campo é obrigatório"),
      otherwise: (schema) => schema.nullable(),
    }),

  dataInicio: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      if (value.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      }
      return false;
    })
    .test(
      "requiredWhenEmptyAndCondition",
      "Este campo é obrigatório!",
      function (value) {
        const { dataFim, universoConsulta, tipoPeriodo, idTipoTransferencia } =
          this.parent;
        const precisaDeData =
          universoConsulta?.id === 2 ||
          (universoConsulta?.id === 3 && idTipoTransferencia?.id === 2) ||
          tipoPeriodo === "D";

        if (precisaDeData && !value && !dataFim) {
          return false;
        }
        return true;
      },
    )
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      },
    ),

  dataFim: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      if (value.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      }
      return false;
    })
    .test(
      "requiredWhenEmptyAndCondition",
      "Este campo é obrigatório!",
      function (value) {
        const {
          dataInicio,
          universoConsulta,
          tipoPeriodo,
          idTipoTransferencia,
        } = this.parent;
        const precisaDeData =
          universoConsulta?.id === 2 ||
          (universoConsulta?.id === 3 && idTipoTransferencia?.id === 2) ||
          tipoPeriodo === "D";

        if (precisaDeData && !value && !dataInicio) {
          return false;
        }
        return true;
      },
    )
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      },
    )
    .test(
      "dataFimAfterInicio",
      "Data fim menor do que data início",
      function (value) {
        const { dataInicio } = this.parent;

        if (!dataInicio || !value) return true;

        const inicio = parse(dataInicio, "dd/MM/yyyy", new Date());
        const fim = parse(value, "dd/MM/yyyy", new Date());

        if (!isValid(inicio) || !isValid(fim)) return true;

        return isAfter(fim, inicio) || isEqual(fim, inicio);
      },
    ),
});

export const FiltersRelatorioCriancaAdolescente = ({
  entidade,
  data,
  setData,
  setLoading,
  roleKeycloack,
  user,
  exclusao,
  setExclusao,
  atualizarDadosTabela,
  loading,

  setMostrarTabela,
}) => {
  const formRef = useRef(null);
  const location = useLocation();
  const initialLoad = useRef(true);
  const auth = useAuth();
  const token = auth?.user?.access_token;
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hintPreliminar, setHintPreliminar] = useState(false);
  const placement = useBreakpointValue({ md: "right" });
  const [universoConsultas] = useState({
    options: [
      { id: 1, label: "Acolhido" },
      { id: 2, label: "Desligado" },
      { id: 3, label: "Transferência" },
      { id: 4, label: "Pendente" },
    ],
    isLoading: false,
  });

  const [tiposTransferencia] = useState({
    options: [
      { id: 1, label: "Entrada por Transferência" },
      { id: 2, label: "Saída por Transferência" },
      {
        id: 6,
        label:
          "Transferência para Acolhedor(a) do mesmo Serviço de Acolhimento",
      },
    ],
    isLoading: false,
  });

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const [initialValues, setInitialValues] = useState({
    universoConsulta: "",
    idsServicosAcolhimento: "",
    dataInicio: "",
    dataFim: "",
    idsMotivosEntrada: "",
    idTipoTransferencia: "",
    idJustificativas: "",
    tipoPeriodo: "",
    entradasPorTransferencias: "",
    saidaTransferencia: "",
    maioresDezoito: "",
    pageSize: 10,
    page: 0,
  });

  const [nomesServicosAcolhimento, setNomesServicosAcolhimento] = useState({
    options: [],
    isLoading: false,
  });

  const [motivos, setMotivos] = useState({ options: [], isLoading: false });

  const [justificativa, setJustificativa] = useState({
    options: [],
    isLoading: false,
  });

  useEffect(() => {
    if (sessionStorage.getItem(entidade + "-filter")) {
      const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));

      setInitialValues(tempObj);
    }
  }, [entidade]);

  useEffect(() => {
    if (!initialLoad.current) {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    } else {
      initialLoad.current = false;
    }
  }, [location]);

  const FocusClearField = () => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (values?.tipoPeriodo === "A") {
        setFieldValue("dataInicio", "");
        setFieldValue("dataFim", "");
      }
    }, [values?.tipoPeriodo, setFieldValue]);

    useEffect(() => {
      if (values?.universoConsulta?.id === 1) {
        setFieldValue("entradasPorTransferencias", ["Possui"]);
      } else if (values?.universoConsulta?.id === 2) {
        setFieldValue("saidaTransferencia", ["Possui"]);
      }
    }, [values?.universoConsulta, setFieldValue]);

    return null;
  };

  const handleGetServicosAcolhimento = useCallback(() => {
    setNomesServicosAcolhimento((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const fetchService =
      entidade === "crianca"
        ? ServicoAcolhimentoService.listarServicosAcolhimentoAtivos
        : ServicoAcolhimentoService.getServicosAcolhimentoAtivosSuspensosLogado;

    fetchService().then((res) => {
      const formattedOptions = res.data.map((item) => ({
        label: item.pessoaJuridica?.nomeFantasia,
        value: item.id,
        regime: item?.tipoServicoAcolhimento?.regime,
      }));

      if (roleKeycloack.includes(criancaFunc.visualizarTudoAcolhimento)) {
        const filteredOptions = res.data
          .filter((item) =>
            user?.orgaoExterno?.includes(item.pessoaJuridica.id),
          )
          .map((item) => ({
            label: item.pessoaJuridica?.nomeFantasia,
            value: item.id,
            regime: item?.tipoServicoAcolhimento?.regime,
          }));

        setInitialValues((prevState) => ({
          ...prevState,
          idsServicosAcolhimento: filteredOptions[0],
        }));
      }

      setNomesServicosAcolhimento({
        options: formattedOptions,
        isLoading: false,
      });
    });
  }, [entidade, roleKeycloack, user?.orgaoExterno]);

  useEffect(() => {
    if (
      entidade === "relatorio-crianca-adolescente" &&
      roleKeycloack.includes(criancaFunc.visualizarTudoAcolhimento)
    ) {
      handleGetServicosAcolhimento();
    }
  }, [handleGetServicosAcolhimento, roleKeycloack, entidade]);

  const isValidDate = (value) => {
    if (!!value) {
      if (value.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const funcPesquisarRelatorioPendencia = useCallback(
    (valuesToSend) => {
      let request = RelatorioService.getRelatorioCrianca(token, valuesToSend);

      request
        .then((res) => {
          sessionStorage.setItem(entidade + "-filterSearch", true);
          const tempObj = JSON.parse(
            sessionStorage.getItem(entidade + "-filter"),
          );

          sessionStorage.setItem(
            entidade + "-filter",
            JSON.stringify({
              ...tempObj,
              totalItems: res.data.totalElements,
              totalPages: res.data.totalPages,
            }),
          );

          setData(res.data);
          setMostrarTabela(true);
          sessionStorage.setItem(
            "mostrarTabelaRelatorio",
            JSON.stringify(true),
          );
        })
        .catch(() => {
          setData([]);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [entidade, setData, setLoading, setMostrarTabela, token],
  );

  useEffect(() => {
    const tempObj = JSON.parse(sessionStorage.getItem("table-" + entidade));
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    const pesquisou = sessionStorage.getItem(entidade + "-filterSearch");
    const passou = sessionStorage.getItem("crianca-passou");
    const servicoPassou = sessionStorage.getItem("servico-passou");

    if (atualizarDadosTabela || (pesquisou && (passou || servicoPassou))) {
      setLoading(true);

      const formatarData = (data) => {
        if (!data) return "";
        if (data.includes("/")) {
          const [dia, mes, ano] = data.split("/");
          return `${ano}-${mes}-${dia}`;
        }
        return data;
      };
      const idsServicoAcolhimentoChange = roleKeycloack.includes(
        criancaFunc.visualizarTudoAcolhimento,
      )
        ? [storage?.idsServicosAcolhimento.value]
        : Array.isArray(storage?.idsServicosAcolhimento)
          ? storage?.idsServicosAcolhimento
              .flat()
              .map((item) => item.value)
              .filter((value) => value !== "")
          : [];

      const valuesToSend = {
        universoConsulta: storage?.universoConsulta
          ? storage?.universoConsulta?.label
              ?.normalize("NFD")
              ?.replace(/[\u0300-\u036f]/g, "")
              ?.toUpperCase()
          : "",
        idsServicosAcolhimento: storage?.idsServicosAcolhimento
          ? idsServicoAcolhimentoChange
          : [],
        dataInicio: storage?.dataInicio ? formatarData(storage.dataInicio) : "",
        dataFim: storage?.dataFim ? formatarData(storage.dataFim) : "",
        idsMotivosEntrada: storage?.idsMotivosEntrada
          ? storage?.idsMotivosEntrada
              .flat()
              .map((item) => item.id)
              .filter((id) => id !== "")
          : [],
        idTipoTransferencia: storage?.idTipoTransferencia
          ? storage?.idTipoTransferencia.id
          : "",
        idJustificativas: storage?.idJustificativas
          ? storage?.idJustificativas
              .flat()
              .map((item) => item.id)
              .filter((id) => id !== "")
          : [],

        tipoPeriodo: storage?.tipoPeriodo === "A" ? "SITUACAO_ATUAL" : "DATA",
        entradasPorTransferencias:
          Array.isArray(storage?.entradasPorTransferencias) &&
          storage.entradasPorTransferencias.includes("Possui"),

        maioresDezoito:
          Array.isArray(storage?.maioresDezoito) &&
          storage.maioresDezoito.includes("Possui"),
        saidaTransferencia:
          Array.isArray(storage?.saidaTransferencia) &&
          storage.saidaTransferencia.includes("Possui"),
        pageSize: storage?.pageSize ? storage.pageSize : "",
        page: storage?.page ? storage.page : "",
        sortField: tempObj ? tempObj[0]?.id : "nomeCrianca",
        sortType: tempObj ? (tempObj[0]?.desc ? "DESC" : "ASC") : "ASC",
      };

      funcPesquisarRelatorioPendencia(valuesToSend);
    }
  }, [
    exclusao,
    atualizarDadosTabela,
    location.state,
    entidade,
    funcPesquisarRelatorioPendencia,
    setLoading,
    roleKeycloack,
  ]);

  const handleGetJustificativa = (id) => {
    setJustificativa({ ...justificativa, isLoading: true });

    JustificativaService.getJustificativa(id)
      .then((res) => {
        const allOption = { id: "", descricao: "Todos" };
        setJustificativa({
          options: [allOption, ...res.data],
          isLoading: false,
        });
      })
      .catch(() => console.log("Erro no get de justificativa"));
  };

  const handleGetMotivos = (props) => {
    setMotivos({ ...motivos, isLoading: true });

    MotivoSituacaoService.listarMotivoSituacao()
      .then((res) => {
        let motivoSituacaoDesligado = res.data.filter(
          (item) => item.tipoSituacao.id === 2 && item.dataFim === null,
        );
        let motivoSituacaoAcolhido = res.data.filter(
          (item) => item.tipoSituacao.id === 1 && item.dataFim === null,
        );

        let options = [];

        if (props?.values?.universoConsulta?.id === 1) {
          options = motivoSituacaoAcolhido;
        } else if (props?.values?.universoConsulta?.id === 2) {
          options = motivoSituacaoDesligado;
        }

        const allOption = { id: "", descricao: "Todos" };
        setMotivos({ options: [allOption, ...options], isLoading: false });
      })
      .catch(() => console.log("Erro no get de motivos"));
  };

  const renderizarTooltip = () => {
    if (isMobile)
      return (
        <PopoverGenero
          isOpen={hintPreliminar}
          setIsOpen={setHintPreliminar}
          flex={true}
          handleClick={() => setHintPreliminar(!hintPreliminar)}
          labelTextoGenero={
            "Na pesquisa por período é possível que existam movimentos antes e depois das datas pesquisadas, porém compreendidas ainda no período da pesquisa."
          }
        />
      );

    return (
      <Tooltip
        label={
          "Na pesquisa por período é possível que existam movimentos antes e depois das datas pesquisadas, porém compreendidas ainda no período da pesquisa."
        }
        fontSize="md"
        maxW="400px"
        textAlign={"justify"}
        placement={placement}
      >
        <span className="hint-tooltip" style={{ zIndex: 0 }}>
          <Icon
            aria-label="Aviso de pendente"
            color="#6B6C71"
            as={IoHelpCircle}
            boxSize={{ base: "20px" }}
          />
        </span>
      </Tooltip>
    );
  };

  const handleSubmit = (values, actions) => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    setTimeout(() => {
      const {
        universoConsulta,
        idsServicosAcolhimento,
        dataInicio,
        dataFim,
        idsMotivosEntrada,
        idTipoTransferencia,
        idJustificativas,
        tipoPeriodo,
        entradasPorTransferencias,
        maioresDezoito,
        saidaTransferencia,
      } = values;

      values.page = 0;
      values.pageSize = storage?.pageSize ? storage.pageSize : 10;

      let parsedDataInicio;
      let formattedDataInicio;
      let parsedDataFim;
      let formattedDataFim;

      const now = new Date();
      const idsServicoAcolhimentoChange = roleKeycloack.includes(
        criancaFunc.visualizarTudoAcolhimento,
      )
        ? [idsServicosAcolhimento.value]
        : Array.isArray(idsServicosAcolhimento)
          ? idsServicosAcolhimento
              .flat()
              .map((item) => item.value)
              .filter((value) => value !== "")
          : [];
      if (dataInicio && isValidDate(dataInicio)) {
        parsedDataInicio = parse(dataInicio, "dd/MM/yyyy", new Date());

        parsedDataInicio.setHours(
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds(),
        );
        formattedDataInicio = parsedDataInicio.toISOString();
      }

      if (dataFim && isValidDate(dataFim)) {
        parsedDataFim = parse(dataFim, "dd/MM/yyyy", new Date());
        parsedDataFim.setHours(
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds(),
        );
        formattedDataFim = parsedDataFim.toISOString();
      }

      const valuesToSend = {
        universoConsulta: universoConsulta
          ? universoConsulta?.label
              ?.normalize("NFD")
              ?.replace(/[\u0300-\u036f]/g, "")
              ?.toUpperCase()
          : "",

        idsServicosAcolhimento: idsServicosAcolhimento
          ? idsServicoAcolhimentoChange
          : [],
        dataInicio: formattedDataInicio ? formattedDataInicio : "",
        dataFim: formattedDataFim ? formattedDataFim : "",
        idsMotivosEntrada: idsMotivosEntrada
          ? idsMotivosEntrada
              .flat()
              .map((item) => item?.id)
              .filter((id) => id !== "")
          : [],
        idTipoTransferencia: idTipoTransferencia ? idTipoTransferencia.id : "",
        idJustificativas: idJustificativas
          ? idJustificativas
              .flat()
              .map((item) => item.id)
              .filter((id) => id !== "")
          : "",
        tipoPeriodo: tipoPeriodo === "A" ? "SITUACAO_ATUAL" : "DATA",
        entradasPorTransferencias:
          Array.isArray(entradasPorTransferencias) &&
          entradasPorTransferencias.includes("Possui"),
        maioresDezoito:
          Array.isArray(maioresDezoito) && maioresDezoito.includes("Possui"),
        saidaTransferencia:
          Array.isArray(saidaTransferencia) &&
          saidaTransferencia.includes("Possui"),
        pageSize: storage?.pageSize ? storage.pageSize : 10,
        page: 0,
      };

      if (entidade === "relatorio-crianca-adolescente") {
        values.desc = false;
        values.sort = "nomeCrianca";
        valuesToSend.sortField = "nomeCrianca";
        valuesToSend.sortType = "ASC";
        sessionStorage.setItem(
          "table-relatorio-crianca-adolescente",
          JSON.stringify([{ id: "nomeCrianca", desc: false }]),
        );
      }

      setLoading(true);
      sessionStorage.setItem(entidade + "-filter", JSON.stringify(values));

      if (entidade === "relatorio-crianca-adolescente") {
        funcPesquisarRelatorioPendencia(valuesToSend);
      }

      actions.setSubmitting(false);
    }, 200);
  };

  return (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        innerRef={formRef}
        enableReinitialize
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <FocusClearField />
            <Flex flexDirection="column" alignItems="center">
              <Grid
                templateColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(12, 2fr)",
                }}
                rowGap="24px"
                columnGap="18px"
                w="100%"
                mt="2px"
              >
                <>
                  <GridItem colSpan={6}>
                    {roleKeycloack?.includes(
                      criancaFunc.visualizarTudoAcolhimento,
                    ) && (
                      <CustomSelectWField
                        fieldName="idsServicosAcolhimento"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isLoading={
                          nomesServicosAcolhimento
                            ? nomesServicosAcolhimento.isLoading
                            : false
                        }
                        options={
                          nomesServicosAcolhimento
                            ? nomesServicosAcolhimento.options
                            : null
                        }
                        label="Serviço de Acolhimento"
                        placeholder="Selecione o Serviço de Acolhimento"
                        isDisabled={roleKeycloack.includes(
                          criancaFunc.visualizarTudoAcolhimento,
                        )}
                        onChange={(value) => {
                          props.setFieldValue("idsServicosAcolhimento", value);
                        }}
                        onFocus={
                          nomesServicosAcolhimento?.options?.length === 0
                            ? handleGetServicosAcolhimento
                            : null
                        }
                      />
                    )}

                    {roleKeycloack?.includes(
                      criancaFunc.visualizarTudoTutelar,
                    ) && (
                      <CustomMultiSelectWField
                        fieldName="idsServicosAcolhimento"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isLoading={
                          nomesServicosAcolhimento
                            ? nomesServicosAcolhimento.isLoading
                            : false
                        }
                        options={
                          nomesServicosAcolhimento
                            ? nomesServicosAcolhimento.options
                            : null
                        }
                        label="Serviço de Acolhimento"
                        placeholder="Selecione o Serviço de Acolhimento"
                        isDisabled={roleKeycloack.includes(
                          criancaFunc.visualizarTudoAcolhimento,
                        )}
                        values={props?.values?.idsServicosAcolhimento[0] || ""}
                        visible
                        onChange={(value) => {
                          props.setFieldValue("idsServicosAcolhimento", value);
                        }}
                        onFocus={
                          nomesServicosAcolhimento?.options?.length === 0
                            ? handleGetServicosAcolhimento
                            : null
                        }
                      />
                    )}
                  </GridItem>
                  <GridItem colSpan={6}>
                    <Flex alignItems={"end"} gap={2}>
                      <CustomSelectWField
                        fieldName="universoConsulta"
                        label="Universo da Consulta*"
                        placeholder="Selecione o Universo da Consulta"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        options={universoConsultas.options}
                        onChange={(value) => {
                          props.setFieldValue(
                            "universoConsulta",
                            value ? value : "",
                          );

                          if (props.values.universoConsulta?.id !== value?.id) {
                            props.setFieldValue("idsMotivosEntrada", "");
                            props.setFieldValue("idTipoTransferencia", "");
                            props.setFieldValue("idJustificativas", "");

                            props.setFieldValue(
                              "entradasPorTransferencias",
                              "",
                            );
                            props.setFieldValue("saidaTransferencia", "");
                          }
                        }}
                      />
                    </Flex>
                  </GridItem>

                  {(props?.values?.universoConsulta?.id === 1 ||
                    props?.values?.universoConsulta?.id === 2) && (
                    <GridItem colSpan={6}>
                      <CustomMultiSelectWField
                        fieldName="idsMotivosEntrada"
                        label="Motivo"
                        placeholder="Selecione o Motivo"
                        getOptionLabel={(option) => option.descricao}
                        getOptionValue={(option) => option.id}
                        isLoading={motivos.isLoading}
                        options={motivos.options}
                        onChange={(value) => {
                          props.setFieldValue(
                            "idsMotivosEntrada",
                            value ? value : "",
                          );
                        }}
                        values={props?.values?.idsMotivosEntrada[0] || ""}
                        onFocus={() => handleGetMotivos(props)}
                        visible
                        todos
                      />
                    </GridItem>
                  )}
                  {props?.values?.universoConsulta?.id === 3 && (
                    <GridItem colSpan={6}>
                      <CustomSelectWField
                        fieldName="idTipoTransferencia"
                        label="Tipo de Transferência*"
                        placeholder="Selecione o Tipo"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        options={
                          (Array.isArray(props.values.idsServicosAcolhimento) &&
                            props.values.idsServicosAcolhimento
                              .flat(Infinity)
                              .some((item) => item?.regime === "SAF")) ||
                          props.values.idsServicosAcolhimento?.regime === "SAF"
                            ? tiposTransferencia.options
                            : tiposTransferencia.options.filter(
                                (opt) => opt.id !== 6,
                              )
                        }
                        onChange={(value) => {
                          props.setFieldValue(
                            "idTipoTransferencia",
                            value ? value : "",
                          );

                          if (
                            props.values.idTipoTransferencia?.id !== value?.id
                          ) {
                            props.setFieldValue("idJustificativas", "");
                          }
                        }}
                      />
                    </GridItem>
                  )}

                  {((props?.values?.universoConsulta?.id === 3 &&
                    (props?.values?.idTipoTransferencia.id === 2 ||
                      props?.values?.idTipoTransferencia.id === 6)) ||
                    props?.values?.universoConsulta?.id === 4) && (
                    <GridItem colSpan={6}>
                      <CustomMultiSelectWField
                        fieldName="idJustificativas"
                        label="Justificativa"
                        placeholder="Selecione a Justificativa"
                        getOptionLabel={(option) => option.descricao}
                        getOptionValue={(option) => option.id}
                        options={justificativa.options}
                        values={props?.values?.idJustificativas[0] || ""}
                        onChange={(value) => {
                          props.setFieldValue("idJustificativas", value);
                        }}
                        onFocus={() =>
                          handleGetJustificativa(
                            props?.values?.universoConsulta?.id === 4
                              ? 5
                              : props?.values?.idTipoTransferencia?.id,
                          )
                        }
                        visible
                        todos
                      />
                    </GridItem>
                  )}

                  <GridItem
                    colSpan={12}
                    border={"solid 1px #007A7F66"}
                    rounded={"lg"}
                    px={"30px"}
                    py={"20px"}
                  >
                    <Flex gap={2} mb={6}>
                      <Text
                        fontWeight={700}
                        fontSize="16px"
                        lineHeight="21.6px"
                        color={"#169499"}
                      >
                        {props?.values?.universoConsulta?.id === 2 ||
                        props?.values?.idTipoTransferencia?.id === 2
                          ? `Informe um Período *`
                          : `Selecione um Período *`}
                      </Text>
                      {props?.values?.universoConsulta?.id === 2 &&
                        renderizarTooltip()}
                    </Flex>

                    {props?.values?.universoConsulta?.id !== 2 &&
                      props?.values?.idTipoTransferencia?.id !== 2 && (
                        <Field name="tipoPeriodo">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.tipoPeriodo &&
                                form.touched.tipoPeriodo
                              }
                              id="tipoPeriodo"
                              width="max-content"
                            >
                              <RadioGroup
                                value={field.value}
                                onChange={(value) =>
                                  props.setFieldValue("tipoPeriodo", value)
                                }
                              >
                                <Stack gap={4}>
                                  <RadioButtonField
                                    fieldName="tipoPeriodo"
                                    value="A"
                                    label="Situação Atual"
                                  />
                                  <Flex gap={3}>
                                    <RadioButtonField
                                      fieldName="tipoPeriodo"
                                      value="D"
                                      label="Por Período"
                                    />
                                    {renderizarTooltip()}
                                  </Flex>
                                </Stack>
                                <FormErrorMessage mr="-14">
                                  {form.errors.tipoPeriodo}
                                </FormErrorMessage>
                              </RadioGroup>
                            </FormControl>
                          )}
                        </Field>
                      )}

                    {(props.values?.tipoPeriodo === "D" ||
                      props?.values?.universoConsulta?.id === 2 ||
                      props?.values?.idTipoTransferencia?.id === 2) && (
                      <Stack
                        direction={{ base: "column", md: "row" }}
                        gap={4}
                        mt={6}
                      >
                        <GridItem colSpan={{ base: 3, sm: 3, lg: 1 }}>
                          <CustomDatepickerWField
                            fieldName="dataInicio"
                            label={`Período de:${
                              !props.values.dataFim &&
                              (props.values.universoConsulta?.id === 2 ||
                                (props.values.universoConsulta?.id === 3 &&
                                  props.values.idTipoTransferencia?.id === 2) ||
                                props.values.tipoPeriodo === "D")
                                ? " *"
                                : ""
                            }`}
                            placeholder="DD/MM/AAAA"
                            maxDate={new Date()}
                          />
                        </GridItem>

                        <GridItem colSpan={{ base: 3, sm: 3, lg: 1 }}>
                          <CustomDatepickerWField
                            fieldName="dataFim"
                            label={`Até:${
                              !props.values.dataInicio &&
                              (props.values.universoConsulta?.id === 2 ||
                                (props.values.universoConsulta?.id === 3 &&
                                  props.values.idTipoTransferencia?.id === 2) ||
                                props.values.tipoPeriodo === "D")
                                ? " *"
                                : ""
                            }`}
                            placeholder="DD/MM/AAAA"
                            maxDate={new Date()}
                          />
                        </GridItem>
                      </Stack>
                    )}
                  </GridItem>

                  <GridItem
                    colSpan={12}
                    border={"solid 1px #007A7F66"}
                    rounded={"lg"}
                    px={"30px"}
                    py={"20px"}
                  >
                    <Text
                      fontWeight={700}
                      fontSize="16px"
                      lineHeight="21.6px"
                      color={"#169499"}
                      mb={6}
                    >
                      Deseja Incluir:
                    </Text>
                    <Stack direction="row" gap={2}>
                      {props?.values?.universoConsulta?.id === 1 && (
                        <Field name="entradasPorTransferencias">
                          {({ field, form }) => (
                            <FormControl
                              id="entradasPorTransferencias"
                              width="max-content"
                            >
                              <Checkbox
                                {...field}
                                isChecked={
                                  props.values?.entradasPorTransferencias ===
                                  true
                                    ? true
                                    : props.values
                                          ?.entradasPorTransferencias === false
                                      ? false
                                      : props.values.entradasPorTransferencias?.includes(
                                            "Possui",
                                          )
                                        ? true
                                        : false
                                }
                                value="Possui"
                                name="entradasPorTransferencias"
                                id="entradasPorTransferencias"
                                whiteSpace="nowrap"
                              >
                                Entradas por Transferências
                              </Checkbox>
                              <FormErrorMessage>
                                {form.errors.entradasPorTransferencias}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      )}

                      {props?.values?.universoConsulta?.id === 2 &&
                        !props.values.idsMotivosEntrada.length && (
                          <Field name="saidaTransferencia">
                            {({ field, form }) => (
                              <FormControl
                                id="saidaTransferencia"
                                width="max-content"
                              >
                                <Checkbox
                                  {...field}
                                  isChecked={
                                    props.values?.saidaTransferencia === true
                                      ? true
                                      : props.values?.saidaTransferencia ===
                                          false
                                        ? false
                                        : props.values.saidaTransferencia?.includes(
                                              "Possui",
                                            )
                                          ? true
                                          : false
                                  }
                                  value="Possui"
                                  name="saidaTransferencia"
                                  id="saidaTransferencia"
                                  whiteSpace="nowrap"
                                >
                                  Saídas por Transferências
                                </Checkbox>
                                <FormErrorMessage>
                                  {form.errors.saidaTransferencia}
                                </FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        )}

                      <Field name="maioresDezoito">
                        {({ field, form }) => (
                          <FormControl id="maioresDezoito" width="max-content">
                            <Checkbox
                              {...field}
                              isChecked={
                                props.values?.maioresDezoito === true
                                  ? true
                                  : props.values?.maioresDezoito === false
                                    ? false
                                    : props.values.maioresDezoito?.includes(
                                          "Possui",
                                        )
                                      ? true
                                      : false
                              }
                              value="Possui"
                              name="maioresDezoito"
                              id="maioresDezoito"
                              whiteSpace="nowrap"
                            >
                              {props?.values?.universoConsulta?.id === 4
                                ? "Os Maiores de 18 Anos que Não se Encontram na Situação de Pendente"
                                : "Maiores de 18 Anos"}
                            </Checkbox>
                            <FormErrorMessage>
                              {form.errors.maioresDezoito}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Stack>
                  </GridItem>
                </>
              </Grid>

              <Button
                type="submit"
                translate="no"
                color="#FFF"
                backgroundColor="#169499"
                fontWeight={700}
                fontSize="15px"
                lineHeight="21.6px"
                _hover={{ bg: "#169499" }}
                isLoading={props.isSubmitting || loading}
                mt={6}
                w={"100%"}
                maxW={{ base: "100%", md: "50%", lg: "17%" }}
              >
                Gerar Relatório
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};
